import { Dispatch, SetStateAction, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import type { AllFeatureIds } from 'generated/featureIds.generated'
import { ItemIntegrationFragment } from 'generated/resources/graphql'

import { useCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import useDeleteMarketplaceCompanyInstallation from 'modules/marketplace/hooks/integrations/companyInstallations/useDeleteMarketplaceCompanyInstallation'
import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import { useMarketplaceUserPermissions } from 'modules/marketplace/hooks/useUserPermissions'
import { useCompanyInstallations } from 'modules/marketplace/store/collections/companyInstallations'

import { OAUTH } from './authorizationTypes'

interface CompanyInstallationProps {
  integration: ItemIntegrationFragment
  oauth_redirect: string
  questionPropertiesPresent: boolean
  setUninstallModalVisible: Dispatch<SetStateAction<boolean>>
  handleUninstall: boolean
  trackEvent: (event: string) => void
}

export const CompanyInstallation = ({
  integration,
  oauth_redirect,
  questionPropertiesPresent,
  setUninstallModalVisible,
  handleUninstall,
  trackEvent,
}: CompanyInstallationProps) => {
  const { id, datoId, installableFf, installable, component, authType } = integration
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [isCompanyInstalled, setIsCompanyInstalled] = useState(false)
  const [installationLoading, setInstallationLoading] = useState(false)
  const companyInstallations = useCompanyInstallations()
  const { canInstallCompanyWideIntegration } = useMarketplaceUserPermissions()
  const deleteMarketplaceCompanyInstallation = useDeleteMarketplaceCompanyInstallation()
  const skipCompanyQuery = component && installable ? false : true

  const {
    data: companyInstallationsData,
    loading,
    refetch: refetchCompanyInstallations,
  } = useGetMarketplaceCompanyInstallations(
    {
      // FIXME: We should arrange the components to call this hook only when there is an id
      integrationIds: [id || -1],
    },
    skipCompanyQuery
  )

  const {
    access: { id: accessId },
    company: { id: companyId },
  } = useCurrentSession()

  useUnsafeEffect(() => {
    setIsCompanyInstalled(companyInstallationsData.length > 0)
  }, [companyInstallationsData, isCompanyInstalled])

  const handleInstall = async () => {
    trackEvent(trackingEvents.marketplace.clickInstallIntegration)
    const payload = {
      access_id: accessId,
      form_data: {},
      company_id: companyId,
      integration_id: id,
    }

    if (authType === OAUTH && questionPropertiesPresent === false) {
      return window.location.replace(oauth_redirect)
    }
    if (questionPropertiesPresent === true)
      return navigate(navigation.discover.marketplace.item.settings.fullPath({ id: datoId }))

    setInstallationLoading(true)
    return asyncAction(
      async () => {
        await companyInstallations.create(payload)
      },
      { loadingMessage: i18n.t('common.loading'), successFlash: true }
    ).then((result) => {
      setInstallationLoading(false)
      result.success && refetchCompanyInstallations()
    })
  }

  const uninstallIntegration = useCallback(() => {
    setInstallationLoading(true)
    return deleteMarketplaceCompanyInstallation(id || -1, companyId, accessId).then((result) => {
      const { success } = handleResult(result, () => undefined)
      success && setIsCompanyInstalled(false)
      setUninstallModalVisible(false)
      setInstallationLoading(false)
    })
  }, [accessId, companyId, id])

  useEffect(() => {
    handleUninstall && uninstallIntegration()
  }, [handleUninstall])

  const handleOpenUninstallModal = () => {
    trackEvent(trackingEvents.marketplace.clickUninstallIntegration)
    setUninstallModalVisible(true)
  }

  if (loading || installationLoading) {
    return <Spinner />
  }

  const installableByFf =
    (installableFf && installableFf !== '' && hasFeature(installableFf as AllFeatureIds)) ||
    installableFf === ''

  if (!installableByFf) return
  if (!canInstallCompanyWideIntegration) return

  if (isCompanyInstalled) {
    return (
      <Box>
        <Button type="secondary" onClick={handleOpenUninstallModal}>
          {i18n.t('marketplace.integrations.company_uninstall')}
        </Button>
      </Box>
    )
  }
  return (
    <Box>
      <Button type="secondary" onClick={handleInstall}>
        {i18n.t('marketplace.integrations.company_install')}
      </Button>
    </Box>
  )
}
